import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  Activity,
  AlertCircle,
  AlertOctagon,
  Banknote,
  CalendarCheck,
  ClipboardList,
  FileText,
  Gauge,
  Home,
  LayoutDashboard,
  Loader2,
  Mail,
  PlayCircle,
  ShieldCheck,
  Star,
  Timer,
  TrendingUp,
  Wrench,
  XCircle,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { api, extractErrorMessage } from '../lib/api';
import { KpiCard } from '../components/reporting/KpiCard';
import { TrendChart } from '../components/reporting/TrendChart';
import { DonutChart } from '../components/reporting/DonutChart';
import { ExportButtons } from '../components/reporting/ExportButtons';
import {
  EMPTY_REPORTING_FILTERS,
  ReportingFilters,
  type ReportingFilterValues,
} from '../components/reporting/ReportingFilters';
import { defaultDateRange, toIsoFromDate, toIsoToDate } from '../components/reporting/dateRange';
import {
  PRIORITE_HEX,
  STATUT_INTERVENTION_HEX,
  STATUT_INTERVENTION_LABEL,
  TYPE_EQUIPEMENT_HEX,
  TYPE_EQUIPEMENT_LABEL,
  formatCurrency,
  formatDurationH,
  formatNumber,
} from '../components/reporting/format';
import type { DashboardResult } from '../components/reporting/types';

/**
 * Page `/dashboard` (Phase 12 — US-DASH-01).
 *
 * Tableau de bord d'accueil multi-modules. Consomme `GET /reporting/dashboard`
 * et affiche :
 *  - bandeaux d'alerte (urgences P0/P1, validations en attente, affectations
 *    équipement en attente) — réutilisés de l'ancien `DashboardPlaceholderPage`.
 *  - 12 KPI cards (équipements, interventions, coûts, satisfaction…).
 *  - section Alertes (étalonnages, maintenances, SLA breach, docs expirants).
 *  - graphiques recharts : tendance interventions / coûts (12 mois), donuts
 *    priorité + type équipement, top services / équipements / techniciens.
 *  - export Excel + PDF (html2pdf.js).
 *
 * Si l'utilisateur n'a pas la permission `reporting.read`, on charge tout de
 * même les bandeaux et on affiche un message d'information à la place du
 * dashboard détaillé.
 */

interface ServiceOption {
  id: string;
  code: string;
  nom_complet: string;
}

interface BatimentOption {
  id: string;
  code: string;
  nom: string;
}

export function DashboardPage() {
  const { user } = useAuth();
  const { can } = usePermissions();
  const canReporting = can('reporting', 'read');

  if (user?.is_platform_admin) {
    return <Navigate to="/platform/tenants" replace />;
  }

  // Bandeaux d'alerte (toujours chargés, dépendent uniquement des permissions
  // métier — préservation du comportement du Placeholder existant).
  const [pendingAffectations, setPendingAffectations] = useState<number>(0);
  const [urgencesCount, setUrgencesCount] = useState<number>(0);
  const [mesValidationsCount, setMesValidationsCount] = useState<number>(0);

  // Filtres + données dashboard.
  const def = defaultDateRange();
  const [filters, setFilters] = useState<ReportingFilterValues>({
    ...EMPTY_REPORTING_FILTERS,
    date_from: def.from,
    date_to: def.to,
  });
  const [data, setData] = useState<DashboardResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [services, setServices] = useState<ServiceOption[]>([]);
  const [batiments, setBatiments] = useState<BatimentOption[]>([]);

  const printRef = useRef<HTMLDivElement | null>(null);

  // ─── Bandeaux ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!user || !can('equipements', 'read')) return;
    api
      .get<{ data: Array<{ id: string; statut_affectation: string }> }>(
        '/equipements/mes-affectations',
      )
      .then((res) => {
        const pending = res.data.data.filter(
          (e) => e.statut_affectation === 'en_attente_acceptation',
        );
        setPendingAffectations(pending.length);
      })
      .catch(() => {
        // silencieux
      });
  }, [user, can]);

  useEffect(() => {
    if (!user || !can('interventions', 'read')) return;
    Promise.all([
      api
        .get<{ data: { urgences_ouvertes?: number } }>('/interventions/stats')
        .then((res) => res.data.data.urgences_ouvertes ?? 0)
        .catch(() => 0),
      api
        .get<{ data: unknown[] }>('/interventions/validations/mes-validations')
        .then((res) => res.data.data.length)
        .catch(() => 0),
    ])
      .then(([urgences, validations]) => {
        setUrgencesCount(urgences);
        setMesValidationsCount(validations);
      })
      .catch(() => {
        // silencieux
      });
  }, [user, can]);

  // ─── Listes services / bâtiments pour les filtres ─────────────────────────
  useEffect(() => {
    if (!canReporting) return;
    api
      .get<{ data: ServiceOption[] }>('/services?statut=actif&page_size=200')
      .then((res) => setServices(res.data.data))
      .catch(() => setServices([]));
    api
      .get<{ data: BatimentOption[] }>('/batiments?page_size=200')
      .then((res) => setBatiments(res.data.data))
      .catch(() => setBatiments([]));
  }, [canReporting]);

  // ─── Chargement dashboard ────────────────────────────────────────────────
  const queryParams = useMemo(() => buildParams(filters), [filters]);

  useEffect(() => {
    if (!canReporting) return;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canReporting, queryParams.toString()]);

  async function refresh() {
    if (!canReporting) return;
    setLoading(true);
    setErrorMessage(null);
    try {
      const qs = queryParams.toString();
      const res = await api.get<{ data: DashboardResult }>(
        `/reporting/dashboard${qs ? `?${qs}` : ''}`,
      );
      setData(res.data.data);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err, 'Erreur lors du chargement du dashboard'));
    } finally {
      setLoading(false);
    }
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      {/* Bandeaux d'alerte */}
      {urgencesCount > 0 && (
        <Link
          to="/interventions/urgences"
          className="block rounded-xl border border-red-200 bg-red-50 p-4 hover:bg-red-100 transition-colors"
        >
          <p className="text-sm font-medium text-red-900 flex items-center">
            <AlertOctagon className="w-4 h-4 mr-2" />
            {urgencesCount} urgence{urgencesCount > 1 ? 's' : ''} P0/P1 en cours
          </p>
          <p className="text-xs text-red-800 mt-1">Cliquez pour consulter la file d'urgences →</p>
        </Link>
      )}
      {mesValidationsCount > 0 && (
        <Link
          to="/mes-validations"
          className="block rounded-xl border border-yellow-200 bg-yellow-50 p-4 hover:bg-yellow-100 transition-colors"
        >
          <p className="text-sm font-medium text-yellow-900 flex items-center">
            <ShieldCheck className="w-4 h-4 mr-2" />
            Vous avez {mesValidationsCount} validation
            {mesValidationsCount > 1 ? 's' : ''} en attente
          </p>
          <p className="text-xs text-yellow-800 mt-1">Cliquez pour répondre →</p>
        </Link>
      )}
      {pendingAffectations > 0 && (
        <Link
          to="/mes-affectations"
          className="block rounded-xl border border-yellow-200 bg-yellow-50 p-4 hover:bg-yellow-100 transition-colors"
        >
          <p className="text-sm font-medium text-yellow-900 flex items-center">
            <Mail className="w-4 h-4 mr-2" />
            Vous avez {pendingAffectations} demande
            {pendingAffectations > 1 ? 's' : ''} d'affectation en attente
          </p>
          <p className="text-xs text-yellow-800 mt-1">Cliquez pour les consulter et y répondre →</p>
        </Link>
      )}

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <LayoutDashboard className="w-7 h-7 mr-2 text-primary-600" />
            Tableau de bord
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Bienvenue, {user.first_name} {user.last_name}
          </p>
        </div>
        {canReporting && (
          <ExportButtons
            excelUrl="/reporting/dashboard.xlsx"
            excelParams={queryParams}
            filename="dashboard"
            targetRef={printRef}
            pdfDisabled={!data}
          />
        )}
      </div>

      {!canReporting ? (
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
          <p className="text-sm text-blue-900">
            Vue détaillée disponible avec la permission « Reporting ».
          </p>
          <p className="text-xs text-blue-700 mt-1">
            Demandez l'accès à un administrateur pour consulter le tableau de bord complet et les
            rapports.
          </p>
        </div>
      ) : (
        <>
          <ReportingFilters
            values={filters}
            onChange={setFilters}
            onRefresh={refresh}
            fields={{ dates: true, service: true, batiment: true }}
            servicesOptions={services.map((s) => ({
              value: s.id,
              label: `${s.code} — ${s.nom_complet}`,
            }))}
            batimentsOptions={batiments.map((b) => ({
              value: b.id,
              label: `${b.code} — ${b.nom}`,
            }))}
          />

          {errorMessage && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{errorMessage}</p>
            </div>
          )}

          {loading && !data ? (
            <div className="flex items-center justify-center p-24 bg-white rounded-lg shadow-sm border border-gray-200">
              <Loader2 className="w-10 h-10 animate-spin text-primary-500" />
            </div>
          ) : data ? (
            <div ref={printRef} className="space-y-4">
              {/* KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiCard
                  label="Équipements"
                  value={formatNumber(data.kpis.total_equipements)}
                  icon={Wrench}
                  tone="green"
                  subtext={`${formatNumber(data.kpis.equipements_en_service)} en service`}
                />
                <KpiCard
                  label="En panne"
                  value={formatNumber(data.kpis.equipements_en_panne)}
                  icon={XCircle}
                  tone="red"
                />
                <KpiCard
                  label="Critiques"
                  value={formatNumber(data.kpis.equipements_critiques)}
                  icon={Gauge}
                  tone="purple"
                  subtext="Vital + critique"
                />
                <KpiCard
                  label="Interventions période"
                  value={formatNumber(data.kpis.total_interventions_periode)}
                  icon={ClipboardList}
                  tone="blue"
                />
                <KpiCard
                  label="En cours"
                  value={formatNumber(data.kpis.interventions_en_cours)}
                  icon={PlayCircle}
                  tone="orange"
                />
                <KpiCard
                  label="Urgences ouvertes"
                  value={formatNumber(data.kpis.interventions_urgences_ouvertes)}
                  icon={AlertOctagon}
                  tone="red"
                  to="/interventions/urgences"
                />
                <KpiCard
                  label="Durée moyenne"
                  value={formatDurationH(data.kpis.duree_moyenne_resolution)}
                  icon={Timer}
                  tone="purple"
                />
                <SatisfactionKpi note={data.kpis.taux_satisfaction} />
                <KpiCard
                  label="Coût total interventions"
                  value={formatCurrency(data.kpis.cout_total_interventions)}
                  icon={Banknote}
                  tone="orange"
                />
                <KpiCard
                  label="Réservations 7 j"
                  value={formatNumber(data.kpis.reservations_a_venir)}
                  icon={CalendarCheck}
                  tone="blue"
                  to="/batiments/reservations"
                />
                <KpiCard
                  label="Documents expirants"
                  value={formatNumber(data.kpis.documents_expirants)}
                  icon={FileText}
                  tone="yellow"
                  subtext="Sous 30 jours"
                />
                <KpiCard
                  label="SLA en breach"
                  value={formatNumber(data.alertes.interventions_sla_breach)}
                  icon={Activity}
                  tone="red"
                />
              </div>

              {/* Alertes */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                <AlertCard
                  label="Étalonnages à renouveler"
                  value={data.alertes.etalonnages_a_renouveler}
                  icon={Activity}
                  tone="orange"
                  to="/equipements"
                />
                <AlertCard
                  label="Maintenances à planifier"
                  value={data.alertes.maintenances_a_planifier}
                  icon={Wrench}
                  tone="purple"
                  to="/equipements"
                />
                <AlertCard
                  label="Documents expirants 30j"
                  value={data.alertes.documents_expirants_30j}
                  icon={FileText}
                  tone="yellow"
                  to="/batiments"
                />
                <AlertCard
                  label="SLA breach"
                  value={data.alertes.interventions_sla_breach}
                  icon={AlertOctagon}
                  tone="red"
                  to="/interventions"
                />
              </div>

              {/* Tendances */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                <h3 className="text-sm font-semibold text-gray-900 flex items-center mb-3">
                  <TrendingUp className="w-4 h-4 mr-2 text-primary-600" />
                  Tendance interventions & coûts (12 mois)
                </h3>
                <TrendChart
                  data={data.tendances.interventions_par_mois.map((row, i) => ({
                    periode: row.periode,
                    count: row.count,
                    cout: data.tendances.cout_par_mois[i]?.cout ?? 0,
                  }))}
                  xKey="periode"
                  series={[
                    { key: 'count', label: 'Interventions', color: '#3b82f6', format: 'number' },
                  ]}
                />
                <TrendChart
                  data={data.tendances.cout_par_mois}
                  xKey="periode"
                  series={[
                    { key: 'cout', label: 'Coût total', color: '#f97316', format: 'currency' },
                  ]}
                  height={200}
                />
              </div>

              {/* Donuts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Panel title="Répartition par priorité (interventions)">
                  <DonutChart
                    data={data.repartitions.interventions_par_priorite.map((p) => ({
                      key: p.priorite,
                      label: p.priorite,
                      value: p.count,
                      color: PRIORITE_HEX[p.priorite] ?? '#6b7280',
                    }))}
                  />
                </Panel>
                <Panel title="Équipements par type">
                  <DonutChart
                    data={data.repartitions.equipements_par_type.map((t) => ({
                      key: t.type,
                      label: TYPE_EQUIPEMENT_LABEL[t.type] ?? t.type,
                      value: t.count,
                      color: TYPE_EQUIPEMENT_HEX[t.type],
                    }))}
                  />
                </Panel>
              </div>

              <Panel title="Répartition par statut (interventions)">
                <DonutChart
                  data={data.repartitions.interventions_par_statut.map((s) => ({
                    key: s.statut,
                    label: STATUT_INTERVENTION_LABEL[s.statut] ?? s.statut,
                    value: s.count,
                    color: STATUT_INTERVENTION_HEX[s.statut] ?? '#6b7280',
                  }))}
                  legendPosition="right"
                />
              </Panel>

              {/* Top 5 services / équipements / techniciens */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <TopBar
                  title="Top 5 services"
                  items={data.top.services_par_interventions.map((s) => ({
                    key: s.service_id,
                    label: `${s.code} — ${s.nom}`,
                    value: s.count,
                  }))}
                />
                <TopBar
                  title="Top 5 équipements"
                  items={data.top.equipements_par_interventions.map((e) => ({
                    key: e.equipement_id,
                    label: `${e.code} — ${e.designation}`,
                    value: e.count,
                  }))}
                />
                <TopBar
                  title="Top 5 techniciens"
                  items={data.top.techniciens_par_interventions.map((t) => ({
                    key: t.user_id,
                    label: t.name,
                    value: t.count,
                  }))}
                />
              </div>

              {/* Pour l'export PDF : footer avec date de génération */}
              <p className="text-[10px] text-gray-400 text-right pt-2">
                Généré le {new Date().toLocaleString('fr-FR')} — Période :{' '}
                {new Date(data.periode.from).toLocaleDateString('fr-FR')} →{' '}
                {new Date(data.periode.to).toLocaleDateString('fr-FR')}
              </p>
            </div>
          ) : (
            <p className="text-sm text-gray-500">Aucune donnée disponible.</p>
          )}
        </>
      )}

      {/* Carte profil compact, conservée pour rappel info utilisateur */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mt-2">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center">
              <Home className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">
                Tenant : {user.tenant?.nom ?? 'Plateforme'}
              </p>
              <p className="text-xs text-gray-500">
                {user.permissions.includes('*')
                  ? 'Super-administrateur (toutes permissions)'
                  : `${user.permissions.length} permission(s) accordée(s)`}
              </p>
            </div>
          </div>
          {user.must_change_password && (
            <Link
              to="/profil"
              className="rounded-md border border-yellow-200 bg-yellow-50 px-3 py-1.5 text-yellow-800 text-xs hover:bg-yellow-100"
            >
              ⚠ Changer votre mot de passe
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Composants internes ────────────────────────────────────────────────────

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
      <h3 className="text-sm font-semibold text-gray-900 mb-3">{title}</h3>
      {children}
    </div>
  );
}

function SatisfactionKpi({ note }: { note: number }) {
  const note5 = note > 5 ? note / 20 : note;
  const rounded = Math.round(note5);
  return (
    <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm h-full">
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-wide text-gray-500">Satisfaction</p>
        <Star className="w-4 h-4 text-yellow-500" />
      </div>
      {note5 === 0 ? (
        <p className="text-2xl font-bold mt-1 text-gray-400">—</p>
      ) : (
        <>
          <p className="text-2xl font-bold mt-1 text-yellow-600">{note5.toFixed(1)}/5</p>
          <div className="flex items-center mt-1">
            {[1, 2, 3, 4, 5].map((i) => (
              <Star
                key={i}
                className={`w-3.5 h-3.5 ${
                  i <= rounded ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function AlertCard({
  label,
  value,
  icon: Icon,
  tone,
  to,
}: {
  label: string;
  value: number;
  icon: React.ComponentType<{ className?: string }>;
  tone: 'red' | 'orange' | 'yellow' | 'purple';
  to?: string;
}) {
  const tones: Record<typeof tone, string> = {
    red: 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100',
    orange: 'border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-100',
    yellow: 'border-yellow-200 bg-yellow-50 text-yellow-700 hover:bg-yellow-100',
    purple: 'border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100',
  };
  const content = (
    <div
      className={`rounded-xl border p-3 transition-colors ${tones[tone]} ${
        to ? 'cursor-pointer' : ''
      }`}
    >
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium">{label}</p>
        <Icon className="w-4 h-4" />
      </div>
      <p className="text-2xl font-bold mt-1">{formatNumber(value)}</p>
    </div>
  );
  return to ? <Link to={to}>{content}</Link> : content;
}

function TopBar({
  title,
  items,
}: {
  title: string;
  items: Array<{ key: string; label: string; value: number }>;
}) {
  const max = items.reduce((m, it) => Math.max(m, it.value), 0);
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
      <h3 className="text-sm font-semibold text-gray-900 mb-3">{title}</h3>
      {items.length === 0 ? (
        <p className="text-xs text-gray-500 py-4 text-center">Aucune donnée sur la période.</p>
      ) : (
        <ul className="space-y-2">
          {items.map((it, idx) => (
            <li key={it.key + idx}>
              <div className="flex items-center justify-between text-sm mb-0.5">
                <span className="text-gray-700 truncate pr-2" title={it.label}>
                  {idx + 1}. {it.label}
                </span>
                <span className="font-semibold text-gray-900">{formatNumber(it.value)}</span>
              </div>
              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary-500 rounded-full"
                  style={{ width: `${max > 0 ? (it.value / max) * 100 : 0}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function buildParams(filters: ReportingFilterValues): URLSearchParams {
  const params = new URLSearchParams();
  const fromIso = toIsoFromDate(filters.date_from);
  const toIso = toIsoToDate(filters.date_to);
  if (fromIso) params.set('date_from', fromIso);
  if (toIso) params.set('date_to', toIso);
  if (filters.service_id) params.set('service_id', filters.service_id);
  if (filters.batiment_id) params.set('batiment_id', filters.batiment_id);
  return params;
}
